import React from "react";
import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";
import { format, parse } from "date-fns";

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 12,
    fontFamily: "Helvetica",
    position: "relative",
  },
  header: {
    marginBottom: 10,
    flexDirection: "row",
    justifyContent: "space-between",
    position: 'relative',
    zIndex: 2,
  },
  headerSection: {
    flex: 1,
  },
  rightHeaderSection: {
    flex: 1,
    marginLeft: 140,
  },
  infoRow: {
    flexDirection: 'row',
    marginBottom: 5,
    alignItems: 'center',
    flexWrap: 'nowrap'
  },
  label: {
    fontSize: 10,
    fontWeight: 'bold',
    marginRight: 5,
    minWidth: 80
  },
  value: {
    fontSize: 10,
    flex: 1
  },
  labelEmergency: {
    fontWeight: "bold",
    fontSize: 10,
    marginRight: 5,
    width: 85,
    color: 'red',
  },
  valueEmergency: {
    fontSize: 10,
    flex: 1,
    color: 'red',
  },
  watermark: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transform: 'rotate(-45deg)',
    fontSize: 72,
    color: 'rgba(255, 0, 0, 0.15)',
    zIndex: 1,
    fontWeight: 'bold',
  },
  watermarkText: {
    textAlign: 'center',
    padding: 20,
  },
  table: {
    marginTop: 10,
    position: 'relative',
    zIndex: 2,
  },
  tableHeader: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderTopWidth: 1,
    borderBottomColor: "#000",
    borderTopColor: "#000",
    backgroundColor: "#f0f0f0",
    padding: 6,
    position: 'relative',
    zIndex: 2,
  },
  tableRow: {
    borderBottomWidth: 1,
    borderBottomColor: "#ccc",
    padding: 8,
    position: 'relative',
    zIndex: 2,
  },
  categoryRow: {
    flexDirection: "row",
    backgroundColor: "rgba(245, 245, 245, 0.9)",
    padding: 8,
    marginTop: 5,
    position: 'relative',
    zIndex: 2,
  },
  sNo: { width: "5%" },
  itemName: { width: "30%" },
  au: { width: "6%" },
  quantity: { width: "8%" },
  stock: { width: "8%" },
  promisedDays: { width: "10%" },
  committedDate: { width: "12%" },
  quantityReceived: { width: "11%" },
  space: { width: '2%' },
  difference: { width: "8%" },
  headerText: {
    fontSize: 8,
    fontWeight: "bold",
  },
  cellText: {
    fontSize: 8,
  },
  rightAlignedCell: {
    fontSize: 8,
    textAlign: "right",
  },
  promisedDaysCell: {
    fontSize: 8,
    textAlign: "center",
  },
  remarksText: {
    fontSize: 8,
    marginTop: 4,
    marginLeft: "5%",
  },
  footerWrapper: {
    position: "absolute",
    bottom: 30,
    left: 30,
    right: 30,
    zIndex: 2,
  },
  footer: {
    fontSize: 8,
    textAlign: "center",
    color: "#666",
  },
  pageNumber: {
    position: 'absolute',
    bottom: 30,
    right: 30,
    fontSize: 8,
    color: '#666',
    zIndex: 2,
  },
  mainHeading: {
    fontSize: 14,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 10,
    textDecoration: 'underline',
    position: 'relative',
    zIndex: 2,
  },
  mainHeadingEmergency: {
    fontSize: 14,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 10,
    textDecoration: 'underline',
    color: 'red',
    position: 'relative',
    zIndex: 2,
  },
  status: {
    fontSize: 12,
    textAlign: "center",
    marginBottom: 15,
    position: 'relative',
    zIndex: 2,
  },
  statusEmergency: {
    fontSize: 12,
    textAlign: "center",
    marginBottom: 15,
    color: 'red',
    position: 'relative',
    zIndex: 2,
  },
  contentWrapper: {
    minHeight: "100%",
    paddingBottom: 50,
    position: 'relative',
    zIndex: 2,
  },
  rowContent: {
    flexDirection: "row",
    alignItems: "center",
  },
  soRemarks: {
    fontSize: 8,
    marginTop: 4,
    marginLeft: "5%",
    color: "#666",
  },
  promisedDaysHeader: {
    width: "10%",
    flexDirection: "column",
    alignItems: "center",
  },
  dateLabel: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 10
  },
  dateValue: {
    fontFamily: 'Helvetica',
    fontSize: 10
  },
  dateContainer: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  dateSeparator: {
    marginHorizontal: 8,
    fontSize: 10
  }
});

const DateText = ({ label, value }) => (
  <>
    <Text style={styles.dateLabel}>{label}: </Text>
    <Text style={styles.dateValue}> {value}</Text>
  </>
);

export const IndentPDF = ({ data }) => {
  console.log('IndentPDF received data:', data);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      // First try parsing as dd/mm/yy
      const parts = dateString.split('/');
      if (parts.length === 3) {
        return dateString; // Already in correct format
      }

      // Try parsing as ISO date string
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "N/A";
      }
      
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = String(date.getFullYear()).slice(-2);
      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error('Date formatting error:', error);
      return "N/A";
    }
  };

  const formatCreationDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = parse(dateString, "MMMM dd, yyyy", new Date());
      return format(date, "dd/MM/yy");
    } catch {
      return dateString;
    }
  };

  const getApprovalDates = (indent) => {
    // Always show creation date
    const creationDate = formatDate(indent.creationDate || indent.CreationDate);
    const l2Raw = indent.L2ApprovalDate || indent.L2ApprovedDate || indent.l2ApprovalDate || indent.l2ApprovedDate;
    const l1Raw = indent.L1ApprovedDate || indent.L1ApprovalDate || indent.l1ApprovedDate || indent.l1ApprovalDate;
    const peRaw = indent.PEApprovalDate || indent.PEApprovedDate || indent.peApprovalDate || indent.peApprovedDate;

    const l2Approved = indent.L2 === 1 || indent.L2 === "Approved";
    const l1Approved = indent.L1 === 1 || indent.L1 === "Approved";
    const peApproved = !!peRaw && peRaw !== "N/A";

    const dates = [];
    dates.push(<DateText key="creation" label="Creation Date" value={creationDate} />);

    if (l2Approved && l2Raw && l2Raw !== "N/A") {
      const l2Date = formatDate(l2Raw);
      dates.push(<Text key="l2-sep" style={styles.dateSeparator}> / </Text>);
      dates.push(<DateText key="l2" label="L2 Date" value={l2Date} />);
      return <View style={styles.dateContainer}>{dates}</View>;
    }

    if (l1Approved && l1Raw && l1Raw !== "N/A") {
      const l1Date = formatDate(l1Raw);
      dates.push(<Text key="l1-sep" style={styles.dateSeparator}> / </Text>);
      dates.push(<DateText key="l1" label="L1 Date" value={l1Date} />);
      return <View style={styles.dateContainer}>{dates}</View>;
    }

    if (peApproved) {
      const peDate = formatDate(peRaw);
      dates.push(<Text key="pe-sep" style={styles.dateSeparator}> / </Text>);
      dates.push(<DateText key="pe" label="PE Date" value={peDate} />);
      return <View style={styles.dateContainer}>{dates}</View>;
    }

    return <View style={styles.dateContainer}>{dates}</View>;
  };

  const indents = Array.isArray(data) ? data : [data];
  const currentDateTime = format(new Date(), "dd/MM/yyyy HH:mm:ss");

  const Footer = () => (
    <View style={styles.footerWrapper} fixed>
      <View style={styles.footer}>
        <Text>© Confident Indent Management System - {currentDateTime}</Text>
      </View>
    </View>
  );

  return (
    <Document>
      {indents.map((indent, pageIndex) => {
        let lastCategory = null;
        const isEmergency = indent.invoicingType?.toLowerCase() === 'emergency';
        const showSOColumns = indent.items.some(item => item.ph === 1);
        const totalPages = indents.length;

        return (
          <Page key={pageIndex} size="A4" style={styles.page}>
            {isEmergency && (
              <View style={styles.watermark}>
                <Text style={styles.watermarkText}>FAST TRACK</Text>
              </View>
            )}
            <View style={styles.contentWrapper}>
              <Text style={isEmergency ? styles.mainHeadingEmergency : styles.mainHeading}>
                Indent Request
              </Text>
              <Text style={isEmergency ? styles.statusEmergency : styles.status}>
                {indent.status || indent.Status}
              </Text>

              <View style={styles.header}>
                <View style={styles.headerSection}>
                  <View style={styles.infoRow}>
                    <Text style={isEmergency ? styles.labelEmergency : styles.label}>Indent #:</Text>
                    <Text style={isEmergency ? styles.valueEmergency : styles.value}>{indent.indentId}</Text>
                  </View>
                  <View style={styles.infoRow}>
                    {getApprovalDates(indent)}
                  </View>
                  <View style={styles.infoRow}>
                    <Text style={isEmergency ? styles.labelEmergency : styles.label}>Status:</Text>
                    <Text style={isEmergency ? styles.valueEmergency : styles.value}>{indent.status}</Text>
                  </View>
                  <View style={styles.infoRow}>
                    <Text style={isEmergency ? styles.labelEmergency : styles.label}>Created By:</Text>
                    <Text style={isEmergency ? styles.valueEmergency : styles.value}>{indent.userName || indent.UserName}</Text>
                  </View>
                </View>
                <View style={styles.rightHeaderSection}>
                  <View style={styles.infoRow}>
                    <Text style={isEmergency ? styles.labelEmergency : styles.label}>Project Name:</Text>
                    <Text style={isEmergency ? styles.valueEmergency : styles.value}>{indent.projectName}</Text>
                  </View>
                  <View style={styles.infoRow}>
                    <Text style={isEmergency ? styles.labelEmergency : styles.label}>Task Scheduled:</Text>
                    <Text style={isEmergency ? styles.valueEmergency : styles.value}>{indent.taskScheduled}</Text>
                  </View>
                  <View style={styles.infoRow}>
                    <Text style={isEmergency ? styles.labelEmergency : styles.label}>Indent Type:</Text>
                    <Text style={isEmergency ? styles.valueEmergency : styles.value}>{indent.invoicingType}</Text>
                  </View>
                </View>
              </View>

              <View style={styles.table}>
                <View style={styles.tableHeader}>
                  <Text style={[styles.sNo, styles.headerText]}>S.No</Text>
                  <Text style={[styles.itemName, styles.headerText]}>Item Name</Text>
                  <Text style={[styles.au, styles.headerText]}>AU</Text>
                  <Text style={[styles.quantity, styles.headerText, styles.rightAlignedCell]}>Qty</Text>
                  <Text style={[styles.stock, styles.headerText, styles.rightAlignedCell]}>Stock</Text>
                  <Text style={[styles.space]}></Text>
                  <View style={styles.promisedDaysHeader}>
                    <Text style={[styles.headerText]}>Promised</Text>
                    <Text style={[styles.headerText]}>Days</Text>
                  </View>
                  <Text style={[styles.committedDate, styles.headerText, styles.rightAlignedCell]}>Committed Date</Text>
                  {showSOColumns && (
                    <>
                      <Text style={[styles.quantityReceived, styles.headerText, styles.rightAlignedCell]}>Rcvd Qty</Text>
                      <Text style={[styles.difference, styles.headerText, styles.rightAlignedCell]}>Diff</Text>
                    </>
                  )}
                </View>

                {indent.items.map((item, index) => {
                  const showCategory = item.category !== lastCategory;
                  lastCategory = item.category;
                  const promisedDaysValue = isEmergency ? item.promisedDays2 : item.promisedDays;

                  return (
                    <View key={index}>
                      {showCategory && item.category && (
                        <View style={styles.categoryRow}>
                          <Text style={[{ width: "100%" }, styles.cellText, { fontWeight: "bold" }]}>
                            Category: {item.category}
                          </Text>
                        </View>
                      )}
                      <View style={styles.tableRow}>
                        <View style={styles.rowContent}>
                          <Text style={[styles.sNo, styles.cellText]}>{index + 1}</Text>
                          <Text style={[styles.itemName, styles.cellText]}>{item.name}</Text>
                          <Text style={[styles.au, styles.cellText]}>{item.au}</Text>
                          <Text style={[styles.quantity, styles.rightAlignedCell]}>{item.quantity}</Text>
                          <Text style={[styles.stock, styles.rightAlignedCell]}>{item.stock}</Text>
                          <Text style={[styles.space]}></Text>
                          <Text style={[styles.promisedDays, styles.promisedDaysCell]}>{promisedDaysValue}</Text>
                          <Text style={[styles.committedDate, styles.cellText, styles.rightAlignedCell]}>
                            {formatDate(item.committedDate)}
                          </Text>
                          {showSOColumns && (
                            <>
                              <Text style={[styles.quantityReceived, styles.rightAlignedCell]}>
                                {item.quantityReceived || '0'}
                              </Text>
                              <Text style={[styles.difference, styles.rightAlignedCell]}>
                                {item.difference || '0'}
                              </Text>
                            </>
                          )}
                        </View>
                        {item.description && item.description.trim() !== "__" && (
                          <Text style={styles.remarksText}>Remarks: {item.description}</Text>
                        )}
                        {item.ph === 1 && item.remarks && (
                          <Text style={styles.soRemarks}>SO Remarks: {item.remarks}</Text>
                        )}
                      </View>
                    </View>
                  );
                })}
              </View>
            </View>
            <Footer />
            <View style={styles.pageNumber} fixed>
              <Text>
                Page {pageIndex + 1} of {totalPages}
              </Text>
            </View>
          </Page>
        );
      })}
    </Document>
  );
};